import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as Mui from "@mui/material";
import {
  Box,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import {
  ColDef,
  ICellRendererParams,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  ValueFormatterParams
} from "ag-grid-community";
import dayjs from "dayjs";
import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import { ContextOperation } from "../../context/OperationContext";
import { useAgGridCustomDatasource } from "../../hooks/useAgGridCustomDatasource";
import useSnackbar from "../../hooks/useSnackbar";
import { ICheckComment, vehicleActions } from "../../service/vehicle.actions";
import { saveFileFromResponse } from "../../utils";
import AgGridOperationTable from "../common/AgGridOperationTable";
import { convertUtcToTzIsoStringWithFormat } from "../../../../util/timezoneConversion";

const EquipmentsModal = (props: any) => {
  const closeModal = props.closeModal;
  const currentVehicle = props.vehicle;
  //console.log('vehicle equipment modal', currentVehicle);
  const timezoneId = currentVehicle?.timeZoneId || "Europe/Rome";
  const { t } = useTranslation(["operation"]);
  const maintenaceAction = vehicleActions();
  const snackbar = useSnackbar();
  const { setLoading, tenant } = useContext(ContextOperation);

  const pageSize = 10;
  const [defaultTotalElements, setDefaultTotalElements] = useState<number>(1);
  const [actualTotalElements, setActualTotalElements] = useState<number>(1);
  const [extraTotalElements, setExtraTotalElements] = useState<number>(1);

  const cancel = () => {
    closeModal(false);
  };

  const defaultEquipmentsColumns: any[] = [
    {
      field: "associated",
      headerName: t("vehicleEquipments.titles.onActual"),
      disableColumnMenu: true,
      flex: 0.9,
      cellRenderer: (props: ICellRendererParams<any>) => {
        return props.value ? (
          <CheckCircleIcon fontSize="inherit" color="success" />
        ) : (
          <CancelIcon fontSize="inherit" color="error" />
        );
      },
    },
    {
      field: "description",
      headerName: t("vehicleEquipments.titles.equipmentDescription"),
      disableColumnMenu: true,
      flex: 1.2,
      cellRenderer: (props: ICellRendererParams<any>) => {
        return (
          <Tooltip title={props.value}>
            <div
              style={{
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              {props.value}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "kitDescription",
      headerName: t("vehicleEquipments.titles.kitDescription"),
      disableColumnMenu: true,
      flex: 1,
      cellRenderer: (props: ICellRendererParams<any>) => {
        return (
          <Tooltip title={props.value}>
            <div
              style={{
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              {props.value}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "checkDatetime",
      headerName: t("vehicleEquipments.titles.checkDatetime"),
      disableColumnMenu: true,
      flex: 1.5,
      cellRenderer: (props: ICellRendererParams<any>) => {
        return (
          <Stack>
            {!!props.data.checkDatetime
              ? convertDateTImeFunction(props?.data?.checkDatetime, timezoneId)
              //dayjs(`${props.data.checkDatetime}`).format("DD/MM/YYYY-HH:mm")
              : ""}
          </Stack>
        );
      },
    },
    {
      field: "_actions",
      headerName: "",
      minWidth: 80,
      maxWidth: 80,
      pinned: "right",
      cellRenderer: (props: ICellRendererParams<any>) => {
        return (
          <Tooltip
            title={t("tooltipTitle.download")}
            color={!!props.data.imagePresent ? "accent" : "primary"}
          >
            <Mui.IconButton
              disabled={!!props.data.imagePresent ? false : true}
              onClick={() => downloadImageMethod(props.data)}
            >
              <FileDownloadIcon />
            </Mui.IconButton>
          </Tooltip>
        );
      },
    },
  ];

  const extraEquipmentsColumns: any[] = [
    {
      field: "code",
      headerName: t("vehicleEquipments.titles.equipmentCode"),
      disableColumnMenu: true,
      flex: 1,
    },
    {
      field: "description",
      headerName: t("vehicleEquipments.titles.equipmentDescription"),
      disableColumnMenu: true,
      flex: 1,
      cellRenderer: (props: ICellRendererParams<any>) => {
        return (
          <Tooltip title={props.value}>
            <div
              style={{
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              {props.value}
            </div>
          </Tooltip>
        );
      },
    },
  ];

  const commentsColumns: ColDef[] = [
    {
      field: "note",
      headerName: t("vehicleEquipments.titles.note"),
      flex: 1,
    },
    {
      field: "createDatetime",
      headerName: t("vehicleEquipments.titles.createDatetime"),
      flex: 1,
      valueFormatter: (params: ValueFormatterParams<ICheckComment>) => {
          return convertDateTImeFunction(params?.data?.createDataTime, timezoneId);
          //return dayjs(params.data?.createDataTime).format("DD/MM/YYYY")
      },
    }
  ];

  const convertDateTImeFunction = function(datetime : any, timezone : any) {
    const utcDatetime = convertUtcToTzIsoStringWithFormat(datetime, timezone, "DD/MM/YYYY");
    if(!utcDatetime) return "";
    else return utcDatetime;
  }

  const [datasourceComments, totalComments] = useAgGridCustomDatasource(
    maintenaceAction.getVehicleCheckComments,
    [tenant, currentVehicle.id],
    []
  )

  const defaultEquipmentDataSource: IServerSideDatasource = useMemo(
    () => ({
      getRows: (params: IServerSideGetRowsParams) => {
        const startRow = params.request.startRow ? params.request.startRow : 0;
        const endRow = params.request.endRow;
        let pageSize = 10;
        if (
          endRow !== null &&
          endRow !== undefined &&
          startRow !== null &&
          startRow !== undefined
        ) {
          pageSize = endRow - startRow;
        }
        const pageNumber = startRow / pageSize;
        setLoading(true);
        maintenaceAction
          .getDefaultEquipment(props.tenant, {
            pageNumber: pageNumber,
            pageSize,
            id: currentVehicle.id,
          })
          .then((response: any) => {
            setLoading(false);
            params.success({
              rowData: response.data.content,
              rowCount: response.data.totalElements,
            });
            setDefaultTotalElements(response.data.totalElements);
          })
          .catch((error) => {
            setDefaultTotalElements(0);
            setLoading(false);
            if (error.status === 400) {
              error.data.subCode === 12
                ? snackbar.show(
                    "error",
                    t("vehicleEquipments.titles.noAssociatedAcriss")
                  )
                : snackbar.show(
                    "error",
                    t("vehicleEquipments.titles.errorMessage")
                  );
            } else snackbar.show("error", error.data.msg);
          });
      },
    }),
    []
  );

  // const actualEquipmentDataSource: IServerSideDatasource = useMemo(
  //   () => ({
  //     getRows: (params: IServerSideGetRowsParams) => {
  //       let startRow = params.request.startRow ? params.request.startRow : 0;
  //       let pageNumber = startRow / pageSize;
  //       maintenaceAction
  //         .getActualEquipment({
  //           pageNumber: pageNumber,
  //           pageSize,
  //           id: currentVehicle.id,
  //         })
  //         .then((response: any) => {
  //           params.success({
  //             rowData: response.data.content,
  //             rowCount: response.data.totalElements,
  //           });
  //           setActualTotalElements(response.data.totalElements);
  //         });
  //     },
  //   }),
  //   []
  // );
  const extraEquipmentDataSource: IServerSideDatasource = useMemo(
    () => ({
      getRows: (params: IServerSideGetRowsParams) => {
        const startRow = params.request.startRow ? params.request.startRow : 0;
        const endRow = params.request.endRow;
        let pageSize = 10;
        if (
          endRow !== null &&
          endRow !== undefined &&
          startRow !== null &&
          startRow !== undefined
        ) {
          pageSize = endRow - startRow;
        }
        const pageNumber = startRow / pageSize;
        setLoading(true);
        maintenaceAction
          .getExtraEquipment(props.tenant, {
            pageNumber: pageNumber,
            pageSize,
            id: currentVehicle.id,
          })
          .then((response: any) => {
            setLoading(false);
            params.success({
              rowData: response.data.content,
              rowCount: response.data.totalElements,
            });
            setExtraTotalElements(response.data.totalElements);
          })
          .catch(() => setLoading(false));
      },
    }),
    []
  );

  const downloadImageMethod = (data: any) => {
    setLoading(true);
    maintenaceAction
      .downloadImage(currentVehicle.id, data.id, props.tenant)
      .then((response: any) => {
        saveFileFromResponse(response);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        snackbar.show("error", error.message);
      });
  };

  return (
    <Box className="dialogClass">
      <Dialog
        PaperProps={{
          sx: {
            minWidth: "899px",
          },
        }}
        open={true}
        aria-labelledby="draggable-dialog-title"
        maxWidth={"md"}
      >
        <DialogTitle
          className="dialogTitleClass"
          style={{ cursor: "move" }}
          id="draggable-dialog-title"
        >
          <Grid
            container
            alignItems="center"
            alignContent="center"
            direction="row"
          >
            <Grid className="maintenanceString" style={{ width: 479 }}>
              {t("carMaintenance.mainTitle.vehicleEquipment")}
            </Grid>
            <Grid xs item>
              <Box>&nbsp;</Box>
            </Grid>

            <Grid item className="plateVimClass">
              {t("carMaintenance.filterAndSearch.plate")}{" "}
              <span style={{ fontWeight: 700 }}>{currentVehicle.plate}</span> -{" "}
              {t("carMaintenance.filterAndSearch.VINfield")}{" "}
              <span style={{ fontWeight: 700 }}>{currentVehicle.vin}</span>
            </Grid>

            <Grid xs item sx={{ justifyContent: "flex-end", display: "flex" }}>
              <IconButton className="cancelIconClass">
                <CloseIcon onClick={cancel} />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>

        <DialogContent dividers style={{ padding: "0" }}>
          <Typography className="tableTitle">
            {t("vehicleEquipments.titles.defaultEquipment")}
          </Typography>
          <AgGridOperationTable
            columnDefs={defaultEquipmentsColumns}
            serverSideDatasource={defaultEquipmentDataSource}
            cacheBlockSize={pageSize}
            serverSideInfiniteScroll={true}
            rowModelType={"serverSide"}
            totalElements={defaultTotalElements}
			emptyState={{
              title: t("vehicleEquipment.pageTitles.noEquipmentTitle"),
              content: t("vehicleEquipments.titles.noDefaultEquipment")
            }}
          />

         
          <Typography className="tableTitle">
            {t("vehicleEquipments.titles.comments")}
          </Typography>
          <AgGridOperationTable 
            columnDefs={commentsColumns}
            serverSideDatasource={datasourceComments}
            cacheBlockSize={pageSize}
            serverSideInfiniteScroll={true}
            rowModelType="serverSide"
            totalElements={totalComments}
            emptyState={{
              title: t("vehicleEquipments.titles.noComments"),
              content: t("vehicleEquipments.titles.noCommentsList")
            }}
          />

         
          <Typography className="tableTitle">
            {t("vehicleEquipments.titles.actualEquipment")}
          </Typography>
           <AgGridOperationTable
            columnDefs={extraEquipmentsColumns}
            serverSideDatasource={extraEquipmentDataSource}
            cacheBlockSize={pageSize}
            serverSideInfiniteScroll={true}
            rowModelType={"serverSide"}
            totalElements={extraTotalElements}
            emptyState={{
              title: t("vehicleEquipment.pageTitles.noEquipmentTitle"),
              content: t("vehicleEquipments.titles.noExtraEquipment")
            }}
          />

        </DialogContent>
      </Dialog>
    </Box>
  );
};
export default EquipmentsModal;
